import {
  createRouter,
  createWebHistory,
  type RouteRecordRaw,
} from "vue-router";
import { useAuthStore } from "@/stores/auth";

declare module "vue-router" {
  interface RouteMeta {
    public?: boolean;
    requiresAuth?: boolean;
    requiresAdmin?: boolean;
    requiresManager?: boolean;
    requiresDesigner?: boolean;
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: "/auth",
    component: () => import("@/layouts/AuthLayout.vue"),
    meta: { public: true },
    children: [
      {
        path: "login",
        name: "login",
        component: () => import("@/views/auth/LoginView.vue"),
      },
      {
        path: "register",
        name: "register",
        component: () => import("@/views/auth/RegisterView.vue"),
      },
    ],
  },
  {
    // Public route — receives an impersonation token in the URL fragment,
    // stashes it in sessionStorage, then forwards into the app.
    path: "/impersonate/handoff",
    name: "impersonate-handoff",
    component: () => import("@/views/ImpersonateHandoffView.vue"),
    meta: { public: true },
  },
  {
    path: "/",
    component: () => import("@/layouts/DashboardLayout.vue"),
    meta: { requiresAuth: true },
    children: [
      {
        path: "",
        name: "dashboard",
        component: () => import("@/views/DashboardView.vue"),
      },
      {
        path: "plants",
        name: "plants",
        component: () => import("@/views/PlantsView.vue"),
      },
      {
        path: "controllers",
        name: "controllers",
        component: () => import("@/views/ControllersView.vue"),
        meta: { requiresAdmin: true },
      },
      {
        path: "controllers/:id",
        name: "controller-detail",
        component: () => import("@/views/ControllerDetailView.vue"),
        props: true,
        meta: { requiresAdmin: true },
      },
      {
        path: "alarms",
        name: "alarms",
        component: () => import("@/views/AlarmsView.vue"),
      },
      {
        path: "measurements",
        name: "measurements",
        component: () => import("@/views/MeasurementsView.vue"),
      },
      {
        path: "pages",
        name: "pages",
        component: () => import("@/views/PagesListView.vue"),
      },
      {
        path: "pages/:id",
        name: "scada-page",
        component: () => import("@/views/ScadaPageView.vue"),
        props: true,
      },
      {
        path: "page-builder",
        name: "page-builder",
        component: () => import("@/views/PageBuilderView.vue"),
        meta: { requiresManager: true },
      },
      {
        path: "logger",
        name: "logger",
        component: () => import("@/views/DataLoggerView.vue"),
        meta: { requiresAdmin: true },
      },
      {
        path: "alarm-rules",
        name: "alarm-rules",
        component: () => import("@/views/AlarmRulesView.vue"),
        meta: { requiresAdmin: true },
      },
      {
        path: "scheduler",
        name: "scheduler",
        component: () => import("@/views/SchedulerView.vue"),
      },
      {
        path: "vpn",
        name: "vpn",
        component: () => import("@/views/VpnView.vue"),
        meta: { requiresAdmin: true },
      },
      {
        path: "graphic-displays",
        name: "graphic-displays",
        component: () => import("@/views/GraphicDisplaysListView.vue"),
      },
      {
        path: "graphic-displays/:id",
        name: "graphic-display",
        component: () => import("@/views/GraphicDisplayView.vue"),
        props: true,
      },
      {
        path: "graphic-builder",
        name: "graphic-builder",
        component: () => import("@/views/GraphicBuilderView.vue"),
        meta: { requiresManager: true },
      },
      {
        path: "audit-log",
        name: "audit-log",
        component: () => import("@/views/AuditLogView.vue"),
        meta: { requiresAdmin: true },
      },
      {
        path: "users",
        name: "users",
        component: () => import("@/views/UsersView.vue"),
        meta: { requiresAdmin: true },
      },
      {
        path: "profile",
        name: "profile",
        component: () => import("@/views/ProfileView.vue"),
      },
      {
        path: "settings",
        name: "settings",
        component: () => import("@/views/BrandingSettingsView.vue"),
        meta: { requiresAdmin: true },
      },
      // ── ElDesign: Electrical/PLC Project Design Tool ──
      // Gated by the "Designer" permission — not visible to regular users.
      {
        path: "el-design",
        name: "el-design",
        component: () => import("@/views/ElDesignView.vue"),
        meta: { requiresDesigner: true },
      },
      {
        path: "el-design/catalog",
        name: "el-catalog",
        component: () => import("@/views/ElCatalogView.vue"),
        meta: { requiresDesigner: true },
      },
      {
        path: "el-design/:id",
        name: "el-project",
        component: () => import("@/views/ElProjectView.vue"),
        props: true,
        meta: { requiresDesigner: true },
      },
    ],
  },
  {
    path: "/:pathMatch(.*)*",
    name: "not-found",
    component: () => import("@/views/NotFoundView.vue"),
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (to) => {
  const auth = useAuthStore();

  // Hydrate user from token on first navigation
  if (!auth.user && localStorage.getItem("cybro_access")) {
    await auth.fetchMe();
  }

  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return { name: "login", query: { next: to.fullPath } };
  }

  // Force password change before anything else when the account was created
  // with a temporary password. /profile hosts the change-password form; logout
  // is the only escape.
  if (
    auth.isAuthenticated
    && auth.user?.must_change_password
    && to.name !== "profile"
    && to.name !== "login"
  ) {
    return { name: "profile" };
  }

  if (to.meta.requiresAdmin && !auth.isServerAdmin) {
    return { name: "dashboard" };
  }

  if (to.meta.requiresManager && !auth.canManagePlants) {
    return { name: "dashboard" };
  }

  if (to.meta.requiresDesigner && !auth.canDesign) {
    return { name: "dashboard" };
  }

  if (auth.isAuthenticated && to.name && ["login", "register"].includes(to.name as string)) {
    return { name: "dashboard" };
  }
});

export default router;
